import type { DesignTokens } from "../types/theme";

const WEB_SYSTEM_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * Base tokens for Light mode.
 *
 * Hex mirror of the OKLCH source of truth in `packages/ui/vars.css` — used by
 * the native/theme resolver. Vigía identity: signal teal accent over cool
 * graphite neutrals; health in the accessible status palette.
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#f1f6f9",
  foreground: "#141d25",
  surface: "#ffffff",
  surfaceForeground: "#141d25",
  surfaceSecondary: "#e9eff2",
  surfaceSecondaryForeground: "#141d25",
  surfaceTertiary: "#dde4e9",
  surfaceTertiaryForeground: "#141d25",
  overlay: "#ffffff",
  overlayForeground: "#141d25",
  muted: "#58656f",
  default: "#e6ecf0",
  defaultForeground: "#141d25",
  accent: "#008693",
  accentForeground: "#f4feff",
  danger: "#dd1323",
  dangerForeground: "#ffffff",
  warning: "#e4990b",
  warningForeground: "#2b1c08",
  success: "#1a9951",
  successForeground: "#f7fbfc",
  separator: "#7b8892",
  glassBackground: "rgba(255, 255, 255, 0.85)",
  glassBorder: "rgba(20, 29, 37, 0.12)",
  glassHighlight: "rgba(20, 29, 37, 0.08)",
  glow0: "rgba(0, 134, 147, 0.13)",
  glow1: "rgba(21, 144, 172, 0.11)",
  glow2: "rgba(26, 153, 81, 0.08)",
  mutedForeground: "#58656f",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(20, 29, 37, 0.1)",
  overlayShadow: "rgba(20, 29, 37, 0.14)",
  fieldShadow: "rgba(20, 29, 37, 0.08)",
  fieldBorder: "rgba(20, 29, 37, 0.16)",
  fieldPlaceholder: "rgba(20, 29, 37, 0.55)",
  fieldBackground: "rgba(20, 29, 37, 0.05)",
  segment: "#ffffff",
  segmentForeground: "#141d25",
  link: "#008693",
  focus: "#008693",
  fieldForeground: "#141d25",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#94a1ab",
  primary: "#008693",
  primaryForeground: "#f4feff",
  secondary: "#e6ecf0",
  secondaryForeground: "#141d25",
  backdrop: "rgba(11, 17, 23, 0.5)",
};

/**
 * Base tokens for Dark mode.
 *
 * Deep graphite control-room base with a luminous signal-cyan accent.
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#131a21",
  foreground: "#f7fbfc",
  surface: "#1c242b",
  surfaceForeground: "#f7fbfc",
  surfaceSecondary: "#252e36",
  surfaceSecondaryForeground: "#f7fbfc",
  surfaceTertiary: "#303942",
  surfaceTertiaryForeground: "#f7fbfc",
  overlay: "#202931",
  overlayForeground: "#f7fbfc",
  muted: "#9aa7b0",
  default: "#2e363e",
  defaultForeground: "#f7fbfc",
  accent: "#00c9d1",
  accentForeground: "#001820",
  danger: "#f4514c",
  dangerForeground: "#ffffff",
  warning: "#f5b33b",
  warningForeground: "#2b1c08",
  success: "#2ec97a",
  successForeground: "#051b0e",
  separator: "#4d5760",
  glassBackground: "rgba(19, 26, 33, 0.55)",
  glassBorder: "rgba(255, 255, 255, 0.12)",
  glassHighlight: "rgba(255, 255, 255, 0.22)",
  glow0: "rgba(0, 201, 209, 0.42)",
  glow1: "rgba(0, 201, 209, 0.38)",
  glow2: "rgba(46, 201, 122, 0.26)",
  mutedForeground: "#9aa7b0",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.3)",
  overlayShadow: "rgba(0, 0, 0, 0.45)",
  fieldShadow: "rgba(0, 0, 0, 0.25)",
  fieldBorder: "rgba(255, 255, 255, 0.12)",
  fieldPlaceholder: "rgba(255, 255, 255, 0.45)",
  fieldBackground: "rgba(255, 255, 255, 0.04)",
  segment: "#3f4952",
  segmentForeground: "#f7fbfc",
  link: "#00c9d1",
  focus: "#00c9d1",
  fieldForeground: "#f7fbfc",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#4d5760",
  primary: "#00c9d1",
  primaryForeground: "#001820",
  secondary: "#2e363e",
  secondaryForeground: "#f7fbfc",
  backdrop: "rgba(0, 0, 0, 0.7)",
};
